/**
 * Created by Simon Zhao on 7/22/21.
 * Description:
 * Provide new sources of actions to reduce state of template fields configuration based on external interactions.
 * ------ maintenance history ------
 *
 */
import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of as observableOf, Observable, forkJoin } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';
import { LoggerService } from '@tamalelibs/services/logger.service';
import { TemplateWebService } from '../../services/template-web.service';
import { GET_ENTITY_FIELDS, GET_ENTITY_FIELDS_FAILURE, GET_ENTITY_FIELDS_SUCCESS, GET_TEMPLATE_FIELDS, GET_TEMPLATE_FIELDS_FAILURE, GET_TEMPLATE_FIELDS_SUCCESS } from '@tamalelibs/redux/template.actions';
import { StoreQuerierService } from '../../services/store-querier.service';
import { entityFieldsSelector, templateFieldsSelector } from '../reducers/template-fields.reducer';
import { TemplateField } from '@tamalelibs/models/template-field.model';
import { AddEntityFieldsToCache, AddTemplateFieldsToCache } from '../actions/template-web.actions';

@Injectable()
export class TemplateFieldsEffects {
    constructor(
        private _actions$: Actions,
        private _templateService: TemplateWebService,
        private _store: Store<any>,
        private _storeQuerier: StoreQuerierService,
    ) { }

    getTemplateFieldsEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(GET_TEMPLATE_FIELDS),
        map(action => action as any),
        map(action => action.payload),
        switchMap((typeId) => {
            const templateFieldsMap: Map<string, Array<TemplateField>> = this._storeQuerier.queryBySelector(templateFieldsSelector);
            if (templateFieldsMap.has(typeId)) {
                const observable = new Observable(subscriber => {
                    subscriber.next(undefined);
                    subscriber.complete();
                });
                return observable.pipe(
                    switchMap(item => {
                        return observableOf({
                            type: GET_TEMPLATE_FIELDS_SUCCESS,
                            payload: [...templateFieldsMap.get(typeId)],
                        });
                    }),
                );
            } else {
                return this._templateService.getTemplateFields(typeId).pipe(
                    map(res => {
                        this._store.dispatch(new AddTemplateFieldsToCache(typeId, res));
                        return {
                            type: GET_TEMPLATE_FIELDS_SUCCESS,
                            payload: res,
                        };
                    }),
                    catchError(err => {
                        return observableOf({
                            type: GET_TEMPLATE_FIELDS_FAILURE,
                            payload: err.message ? err.message : err.toString()
                        });
                    }
                    ),
                );
            }
        })));

    getEntityFieldsEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(GET_ENTITY_FIELDS),
        map(action => action as any),
        map(action => action.payload),
        switchMap((typeId) => {
            const entityFieldsMap: Map<string, Array<TemplateField>> = this._storeQuerier.queryBySelector(entityFieldsSelector);
            if (entityFieldsMap && entityFieldsMap.has(typeId)) {
                const observable = new Observable(subscriber => {
                    subscriber.next(undefined);
                    subscriber.complete();
                });
                return observable.pipe(
                    switchMap(item => {
                        return observableOf({
                            type: GET_ENTITY_FIELDS_SUCCESS,
                            payload: [...entityFieldsMap.get(typeId)],
                        });
                    }),
                );
            } else {
                return this._templateService.getEntityFields(typeId).pipe(
                    map(res => {
                        this._store.dispatch(new AddEntityFieldsToCache(typeId, res));
                        return {
                            type: GET_ENTITY_FIELDS_SUCCESS,
                            payload: res,
                        };
                    }),
                    catchError(err => {
                        return observableOf({
                            type: GET_ENTITY_FIELDS_FAILURE,
                            payload: err.message ? err.message : err.toString()
                        });
                    }
                    ),
                );
            }
        })));
}
